import { Application, Container, Graphics, Text } from "pixi.js";
import { balance, type PlayerState } from "./types.ts";

// Nameplate above each sprite. The Text node is parented to the world
// container so it tracks the player's world coordinates (camera handles
// the screen mapping). Empty displayName → label hidden.
type PlayerSprite = {
  id: string;
  gfx: Graphics;
  label: Text;
};

const NAMEPLATE_OFFSET_PX = 22; // contracts:allow-literal — sits just above the sprite top edge
const NAMEPLATE_FONT_SIZE = 12; // contracts:allow-literal — UI label sizing

export class Renderer {
  readonly app = new Application();
  private readonly world = new Container();
  private readonly ui = new Container();
  private readonly players = new Map<string, PlayerSprite>();
  private localPlayerId: string | null = null;

  async init(mount: HTMLElement): Promise<void> {
    await this.app.init({
      resizeTo: window,
      background: "#1a1a1a",
      antialias: true,
    });
    mount.appendChild(this.app.canvas);

    this.app.stage.addChild(this.world);
    this.app.stage.addChild(this.ui);

    this.drawTilemap();
  }

  // Exposed so world-space overlays (nodes, effects) can insert themselves
  // under the player layer without Renderer knowing their internals.
  get worldContainer(): Container {
    return this.world;
  }

  setLocalPlayerId(playerId: string): void {
    this.localPlayerId = playerId;
  }

  addPlayer(state: PlayerState): void {
    if (this.players.has(state.playerId)) {
      this.updatePlayer(state.playerId, state.x, state.y);
      this.setDisplayName(state.playerId, state.displayName);
      return;
    }
    const size = balance.player.sizePx;
    const half = size / 2; // contracts:allow-literal — centering idiom
    const gfx = new Graphics()
      .rect(-half, -half, size, size)
      .fill({ color: state.color });
    gfx.x = state.x;
    gfx.y = state.y;
    this.world.addChild(gfx);
    const label = new Text({
      text: state.displayName,
      style: { fontSize: NAMEPLATE_FONT_SIZE, fill: 0xffffff, fontFamily: "monospace", stroke: { color: 0x000000, width: 3 } }, // contracts:allow-literal — outline width
    });
    label.anchor.set(0.5, 1); // contracts:allow-literal — bottom-center anchor for offset math
    label.x = state.x;
    label.y = state.y - NAMEPLATE_OFFSET_PX;
    label.visible = state.displayName !== "";
    this.world.addChild(label);
    this.players.set(state.playerId, { id: state.playerId, gfx, label });
  }

  updatePlayer(playerId: string, x: number, y: number): void {
    const sprite = this.players.get(playerId);
    if (!sprite) return;
    sprite.gfx.x = x;
    sprite.gfx.y = y;
    sprite.label.x = x;
    sprite.label.y = y - NAMEPLATE_OFFSET_PX;
  }

  setDisplayName(playerId: string, displayName: string): void {
    const sprite = this.players.get(playerId);
    if (!sprite) return;
    sprite.label.text = displayName;
    sprite.label.visible = displayName !== "";
  }

  removePlayer(playerId: string): void {
    const sprite = this.players.get(playerId);
    if (!sprite) return;
    sprite.gfx.destroy();
    sprite.label.destroy();
    this.players.delete(playerId);
  }

  hasPlayer(playerId: string): boolean {
    return this.players.has(playerId);
  }

  playerIds(): IterableIterator<string> {
    return this.players.keys();
  }

  // Center the view on the local player, clamped so the camera never
  // shows outside the zone bounds. If the viewport is larger than the
  // zone on either axis, center that axis instead of clamping.
  updateCamera(): void {
    if (!this.localPlayerId) return;
    const sprite = this.players.get(this.localPlayerId);
    if (!sprite) return;

    const viewW = this.app.renderer.width;
    const viewH = this.app.renderer.height;
    const zoneW = balance.world.widthPx;
    const zoneH = balance.world.heightPx;

    let worldX: number;
    if (viewW >= zoneW) {
      worldX = (viewW - zoneW) / 2; // contracts:allow-literal — centering idiom
    } else {
      const target = viewW / 2 - sprite.gfx.x; // contracts:allow-literal — centering idiom
      const minX = viewW - zoneW;
      const maxX = 0;
      worldX = Math.max(minX, Math.min(maxX, target));
    }

    let worldY: number;
    if (viewH >= zoneH) {
      worldY = (viewH - zoneH) / 2; // contracts:allow-literal — centering idiom
    } else {
      const target = viewH / 2 - sprite.gfx.y; // contracts:allow-literal — centering idiom
      const minY = viewH - zoneH;
      const maxY = 0;
      worldY = Math.max(minY, Math.min(maxY, target));
    }

    this.world.x = worldX;
    this.world.y = worldY;
  }

  private drawTilemap(): void {
    // A single Graphics object for the entire tilemap is far cheaper than
    // one per tile. Alternating shades give a checkerboard reference so
    // motion is visually obvious on the otherwise flat background.
    const map = new Graphics();
    const tile = balance.world.tileSizePx;
    const cols = Math.ceil(balance.world.widthPx / tile);
    const rows = Math.ceil(balance.world.heightPx / tile);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const shade = (r + c) % 2 === 0 // contracts:allow-literal — `% 2` checkerboard alternation, not a tunable
          ? balance.rendering.tileShadeLight
          : balance.rendering.tileShadeDark;
        map.rect(c * tile, r * tile, tile, tile).fill({ color: shade });
      }
    }
    this.world.addChildAt(map, 0);
  }
}
